import { type Area, AreaCreateSchema, type AreaUpdate, AreaUpdateSchema } from '@gestion-permisos/contracts';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ADMIN_PAGE_SIZE,
  useAreasPage,
  useCreateArea,
  useRereadArea,
  useUpdateArea,
} from '../admin/admin-api.js';
import {
  BackToAdmin,
  Checkbox,
  describedBy,
  Field,
  firstForbidden,
  INPUT,
  isStale,
  Notice,
  nullIfBlank,
} from '../admin/admin-ui.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { Card } from '../components/card.js';
import { Modal, ModalFooter } from '../components/modal.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { pageFrom, Pagination, PastLastPage, TABLE, TD, TH, usePageHref } from '../requests/request-list.js';

const NAME_ERROR = 'Escribe un nombre (hasta 200 caracteres).';
const DESCRIPTION_ERROR = 'La descripción admite hasta 2000 caracteres.';

/** Botones de esta pantalla, con el aspecto de las ya adaptadas (`admin-ui.tsx` conserva los suyos). */
const PRIMARY = buttonClasses();
const OUTLINE = buttonClasses('outline');

/** Errores de forma del esquema del contrato, por campo, con texto fijo. */
function shapeErrors(issues: { path: PropertyKey[] }[]) {
  const errors: { name?: string; description?: string } = {};
  for (const issue of issues) {
    if (issue.path[0] === 'name') errors.name = NAME_ERROR;
    if (issue.path[0] === 'description') errors.description = DESCRIPTION_ERROR;
  }
  return errors;
}

/**
 * Áreas (W7): una página de `GET /areas`; crear (`POST`) y editar o desactivar (`PATCH` con `If-Match`, solo
 * lo cambiado). La forma se valida con el esquema del contrato antes de enviar; las reglas (nombre único,
 * área con empleados…) las decide la API: un 409 se muestra tal cual y un 412 vuelve a leer el área.
 *
 * Igual que tipos de permiso: cabecera, la tabla en una tarjeta con «Crear» en su cabecera, y el alta y la
 * edición en ventanas (`Modal`). Cerrar una ventana no envía nada.
 */
export function AdminAreasPage() {
  const [params] = useSearchParams();
  const page = pageFrom(params, 'pagina');
  const pageHref = usePageHref('pagina');
  const areas = useAreasPage(page);
  const create = useCreateArea();
  const update = useUpdateArea();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Area | undefined>(undefined);
  const [saved, setSaved] = useState<string | undefined>(undefined);
  const startEditing = (area: Area) => {
    // Un error de otra edición no se arrastra a la nueva.
    update.reset();
    setSaved(undefined);
    setEditing(area);
  };
  const openCreate = () => {
    // Un error de un alta anterior no se arrastra a la nueva.
    create.reset();
    setSaved(undefined);
    setCreating(true);
  };

  const denied = firstForbidden(areas.error, create.error, update.error);
  if (denied) return <ApiErrorView error={denied} />;

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <BackToAdmin />
      <PageHeader title="Áreas" description="Crear, editar y desactivar áreas." />
      <Notice text={saved} />

      <div>
        {areas.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
        {areas.isError ? <ApiErrorView error={areas.error} /> : null}
        {areas.isSuccess ? (
          <>
            <Card flush>
              <div className="flex justify-end border-b border-border p-4">
                <button type="button" className={PRIMARY} onClick={openCreate}>
                  <Plus aria-hidden className="size-4" />
                  Crear
                </button>
              </div>
              {areas.data.total === 0 ? (
                <p className="px-5 py-16 text-center text-sm text-muted-foreground">Todavía no hay áreas.</p>
              ) : areas.data.items.length === 0 ? (
                <div className="p-5">
                  <PastLastPage firstPage={pageHref(1)} what="áreas" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={TABLE} aria-label="Áreas">
                    <thead className="border-b border-border bg-muted text-muted-foreground">
                      <tr>
                        <th scope="col" className={TH}>Nombre</th>
                        <th scope="col" className={TH}>Descripción</th>
                        <th scope="col" className={TH}>Estado</th>
                        <th scope="col" className={TH}>
                          <span className="sr-only">Acciones</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {areas.data.items.map((area) => (
                        <tr key={area.id} className="border-t border-border first:border-t-0">
                          <th scope="row" className={TH}>{area.name}</th>
                          <td className={TD}>{area.description ?? '—'}</td>
                          <td className={TD}>{area.active ? 'Activa' : 'Inactiva'}</td>
                          <td className={TD}>
                            <button type="button" className={OUTLINE} onClick={() => startEditing(area)} aria-label={`Editar ${area.name}`}>
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            {areas.data.total > 0 ? (
              <Pagination page={page} total={areas.data.total} pageSize={ADMIN_PAGE_SIZE} hrefFor={pageHref} units={['área', 'áreas']} />
            ) : null}
          </>
        ) : null}
      </div>

      {creating ? (
        <Modal title="Nueva área" onClose={() => setCreating(false)}>
          <CreateAreaForm
            create={create}
            onCancel={() => setCreating(false)}
            onCreated={(area) => {
              setCreating(false);
              setSaved(`Área «${area.name}» creada.`);
            }}
          />
        </Modal>
      ) : null}

      {editing ? (
        <Modal title={`Editar «${editing.name}»`} onClose={() => setEditing(undefined)}>
          <EditAreaForm
            // Una versión nueva (tras un 412) recarga el formulario; la ventana sigue abierta.
            key={`${editing.id}-${editing.version}`}
            area={editing}
            update={update}
            onClose={() => setEditing(undefined)}
            onReloaded={setEditing}
            onSaved={(area) => {
              setEditing(undefined);
              setSaved(`Cambios guardados en «${area.name}».`);
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

function CreateAreaForm({
  create,
  onCancel,
  onCreated,
}: {
  create: ReturnType<typeof useCreateArea>;
  onCancel: () => void;
  /** Tras el alta se cierra la ventana y la página muestra «Área «{nombre}» creada.». */
  onCreated: (area: Area) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = AreaCreateSchema.safeParse({ name, description: nullIfBlank(description) });
    if (!parsed.success) {
      setErrors(shapeErrors(parsed.error.issues));
      return;
    }
    setErrors({});
    create.mutate(parsed.data, { onSuccess: onCreated });
  };

  return (
    <form noValidate onSubmit={submit} className="space-y-3" aria-label="Nueva área">
      {create.error ? <ProblemMessage error={create.error} /> : null}
      <Field id="area-name" label="Nombre" error={errors.name}>
        <input id="area-name" value={name} onChange={(event) => setName(event.target.value)} className={INPUT} {...describedBy('area-name', errors.name)} />
      </Field>
      <Field id="area-description" label="Descripción" error={errors.description}>
        <textarea
          id="area-description"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          className={INPUT}
          {...describedBy('area-description', errors.description)}
        />
      </Field>
      <ModalFooter>
        <button type="button" className={OUTLINE} onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" disabled={create.isPending} className={PRIMARY}>
          Crear área
        </button>
      </ModalFooter>
    </form>
  );
}

function EditAreaForm({
  area,
  update,
  onClose,
  onReloaded,
  onSaved,
}: {
  area: Area;
  update: ReturnType<typeof useUpdateArea>;
  onClose: () => void;
  /** Tras un 412: la versión actual del área, para recargar el formulario. */
  onReloaded: (area: Area) => void;
  onSaved: (area: Area) => void;
}) {
  const reread = useRereadArea();
  const [name, setName] = useState(area.name);
  const [description, setDescription] = useState(area.description ?? '');
  const [active, setActive] = useState(area.active);
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const changes: AreaUpdate = {};
    if (name !== area.name) changes.name = name;
    if (nullIfBlank(description) !== area.description) changes.description = nullIfBlank(description);
    if (active !== area.active) changes.active = active;
    if (Object.keys(changes).length === 0) {
      setNotice('No hay cambios que guardar.');
      return;
    }
    const parsed = AreaUpdateSchema.safeParse(changes);
    if (!parsed.success) {
      setErrors(shapeErrors(parsed.error.issues));
      return;
    }
    setErrors({});
    setNotice(undefined);
    update.mutate(
      { id: area.id, version: area.version, changes: parsed.data },
      {
        onSuccess: onSaved,
        onError: (error) => {
          if (!isStale(error)) return;
          // 412: otra persona cambió el área. Se vuelve a leer y el formulario se recarga con la versión actual.
          void reread(area.id)
            .then(onReloaded)
            .catch(() => undefined);
        },
      },
    );
  };

  return (
    <form noValidate onSubmit={submit} className="space-y-3" aria-label={`Editar ${area.name}`}>
      <Notice text={notice} />
      {update.error ? (
        <>
          {isStale(update.error) ? <Notice text="Otra persona cambió el área. Se cargaron los datos actuales: revísalos y vuelve a guardar." /> : null}
          <ProblemMessage error={update.error} />
        </>
      ) : null}
      <Field id="edit-area-name" label="Nombre" error={errors.name}>
        <input id="edit-area-name" value={name} onChange={(event) => setName(event.target.value)} className={INPUT} {...describedBy('edit-area-name', errors.name)} />
      </Field>
      <Field id="edit-area-description" label="Descripción" error={errors.description}>
        <textarea
          id="edit-area-description"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          className={INPUT}
          {...describedBy('edit-area-description', errors.description)}
        />
      </Field>
      <Checkbox id="edit-area-active" label="Activa" checked={active} onChange={setActive} />
      <ModalFooter>
        <button type="button" className={OUTLINE} onClick={onClose}>
          Cancelar
        </button>
        <button type="submit" disabled={update.isPending} className={PRIMARY}>
          Guardar cambios
        </button>
      </ModalFooter>
    </form>
  );
}
